import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Star, Trophy, Clock, Lock, Settings, Download, FileText, RefreshCw } from "lucide-react";
import DeadlineBanner from "@/components/DeadlineBanner";
import { GameweekSelect, pickCurrentGw } from "@/components/GameweekSelect";

function FixtureCard({ fixture, prediction, locked, onSave, jokerOn = true }) {
  const [h, setH] = useState(prediction?.home_goals ?? "");
  const [a, setA] = useState(prediction?.away_goals ?? "");
  const [isJoker, setIsJoker] = useState(!!prediction?.is_joker);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setH(prediction?.home_goals ?? "");
    setA(prediction?.away_goals ?? "");
    setIsJoker(!!prediction?.is_joker);
    setJustSaved(false);
  }, [prediction]);

  const submit = async () => {
    if (h === "" || a === "") { setMsg("Enter both scores"); return; }
    setBusy(true); setMsg("");
    try {
      await onSave({ fixture_id: fixture.id, home_goals: Number(h), away_goals: Number(a), is_joker: isJoker });
      setJustSaved(true);
      setMsg("");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  // A prediction is "saved" when we have a stored value and the local inputs
  // match it exactly (both goals and joker flag). Any local edit flips it back
  // to unsaved so the button re-lights the accent green.
  const hasStored = prediction && prediction.home_goals !== null && prediction.home_goals !== undefined;
  const inSync = hasStored
    && String(prediction.home_goals) === String(h)
    && String(prediction.away_goals) === String(a)
    && !!prediction.is_joker === !!isJoker;
  const isSaved = inSync && (justSaved || !!hasStored);

  const complete = fixture.status === "complete";
  const isLive = !!fixture.is_live && !complete;
  const showScore = complete || (isLive && fixture.home_score !== null && fixture.home_score !== undefined);
  const points = prediction?.points_awarded ?? 0;

  return (
    <div className={`p-4 rounded-2xl bg-[#0D2038] border ${isJoker ? "border-[#F5B942]/40 joker-glow" : "border-[#1F3A5C]"} ${isLive ? "ring-1 ring-red-500/40" : ""}`} data-testid={`fixture-card-${fixture.id}`}>
      <div className="flex items-center justify-between text-xs text-[#AAB7C7] mb-3">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {fixture.kickoff_at ? new Date(fixture.kickoff_at).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "TBD"}
        </span>
        <span className="uppercase tracking-wider">
          {isLive ? (
            <span className="text-red-400 inline-flex items-center gap-1" data-testid="live-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              LIVE{fixture.live_minute ? ` · ${fixture.live_minute}'` : ""}
            </span>
          ) : complete ? <span className="text-[#C7F500]">Final</span>
            : locked ? <span className="text-red-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>
            : "Open"}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 text-right">
          <div className="font-heading font-semibold">{fixture.home_team}</div>
        </div>
        <div className="flex items-center gap-2">
          {showScore ? (
            <div className="flex items-center gap-2">
              <div className={`score-input flex items-center justify-center ${isLive ? "text-red-400" : "text-[#C7F500]"}`}>{fixture.home_score}</div>
              <span className="text-[#7A8AA3]">-</span>
              <div className={`score-input flex items-center justify-center ${isLive ? "text-red-400" : "text-[#C7F500]"}`}>{fixture.away_score}</div>
            </div>
          ) : (
            <>
              <input type="number" min={0} value={h} onChange={(e) => setH(e.target.value)} disabled={locked} data-testid={`predict-home-${fixture.id}`} className="score-input" />
              <span className="text-[#7A8AA3]">-</span>
              <input type="number" min={0} value={a} onChange={(e) => setA(e.target.value)} disabled={locked} data-testid={`predict-away-${fixture.id}`} className="score-input" />
            </>
          )}
        </div>
        <div className="flex-1 text-left">
          <div className="font-heading font-semibold">{fixture.away_team}</div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        {jokerOn ? (
        <button
          type="button"
          onClick={() => setIsJoker((v) => !v)}
          disabled={locked || complete}
          data-testid={`joker-toggle-${fixture.id}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isJoker ? "bg-[#F5B942]/15 border-[#F5B942] text-[#F5B942]" : "bg-[#132A45] border-[#1F3A5C] text-[#AAB7C7] hover:text-white"} ${(locked || complete) ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <Star className={`w-3.5 h-3.5 ${isJoker ? "fill-[#F5B942] joker-pulse" : ""}`} /> Joker
        </button>
        ) : <span />}

        {complete && prediction ? (
          <div className="text-xs">
            <span className="text-[#AAB7C7]">your pick: {prediction.home_goals}-{prediction.away_goals}</span>
            <span className={`ml-2 font-bold ${points > 0 ? "text-[#C7F500]" : "text-red-400"}`}>+{points}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {msg && <span className="text-xs text-red-400">{msg}</span>}
            {!locked && <button onClick={submit} disabled={busy} data-testid={`save-prediction-${fixture.id}`}
              className={`px-4 py-1.5 rounded-lg disabled:opacity-50 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
                isSaved
                  ? "bg-[#132A45] border border-[#2E6E4E] text-[#7AE0A0] hover:bg-[#193253]"
                  : "bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426]"
              }`}>
              {busy ? "Saving…" : isSaved ? (<><span aria-hidden>✓</span> Saved</>) : "Save"}
            </button>}
          </div>
        )}
      </div>
    </div>
  );
}

function LeaderboardTable({ rows, currentUserId, columns = ["pos", "name", "points", "exact", "joker"] }) {
  if (!rows.length) return <p className="text-sm text-[#AAB7C7] p-4">No data yet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="leaderboard-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-[#AAB7C7] border-b border-[#1F3A5C]">
            {columns.includes("pos") && <th className="text-left py-3 pl-3 w-12">#</th>}
            {columns.includes("name") && <th className="text-left py-3">Player</th>}
            {columns.includes("points") && <th className="text-right py-3">Pts</th>}
            {columns.includes("exact") && <th className="text-right py-3 hidden sm:table-cell">Exact</th>}
            {columns.includes("joker") && <th className="text-right py-3 hidden sm:table-cell">Joker</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} className={`border-b border-[#1F3A5C] ${r.user_id === currentUserId ? "bg-[#132A45]" : ""}`}>
              {columns.includes("pos") && <td className="py-3 pl-3 font-mono text-[#AAB7C7]">{r.position}</td>}
              {columns.includes("name") && <td className="py-3 font-medium">{r.name}</td>}
              {columns.includes("points") && <td className="py-3 text-right font-heading font-bold">{r.total_points}</td>}
              {columns.includes("exact") && <td className="py-3 text-right text-[#AAB7C7] hidden sm:table-cell">{r.exact_scores}</td>}
              {columns.includes("joker") && <td className="py-3 text-right text-[#F5B942] hidden sm:table-cell">{r.joker_wins}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CompetitionView() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "fixtures";

  const [comp, setComp] = useState(null);
  const [gameweeks, setGameweeks] = useState([]);
  const [activeGw, setActiveGw] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [lbSeason, setLbSeason] = useState([]);
  const [lbWeekly, setLbWeekly] = useState([]);
  const [lbMonthly, setLbMonthly] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedMonthId, setSelectedMonthId] = useState("");
  const [stats, setStats] = useState(null);

  const loadComp = async () => {
    const { data } = await api.get(`/competitions/${id}`);
    setComp(data);
  };
  const loadGameweeks = async () => {
    const { data } = await api.get(`/competitions/${id}/gameweeks`);
    setGameweeks(data);
    if (!activeGw && data.length) setActiveGw(pickCurrentGw(data));
  };

  const loadMonths = async () => {
    const { data } = await api.get(`/competitions/${id}/months`);
    setMonths(data);
  };

  useEffect(() => { loadComp(); loadGameweeks(); loadMonths(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!comp || !activeGw) return;
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const [fx, mp] = await Promise.all([
          api.get(`/competitions/${id}/fixtures?gameweek_id=${activeGw.id}`),
          api.get(`/predictions/mine?competition_id=${id}&gameweek_id=${activeGw.id}`),
        ]);
        if (cancelled) return;
        setFixtures(fx.data);
        setPredictions(mp.data);
      } catch { /* ignore transient errors */ }
    };
    fetchLive();
    return () => { cancelled = true; };
  }, [comp, activeGw, id]);

  // Bootstrap the "should we be polling?" check using kickoff times too — so we
  // start refreshing even *before* the backend has marked a fixture as is_live.
  // Wide window: fixtures often inherit the gameweek Friday-6pm deadline as
  // their kickoff, but matches actually play Sat/Sun — so we poll for any
  // non-complete fixture within a 48h window either side of the stored kickoff.
  const nowMs = Date.now();
  const inLiveWindow = fixtures.some((f) => {
    if (f.status === "complete") return false;
    if (f.is_live) return true;
    if (!f.kickoff_at) return false;
    const ko = new Date(f.kickoff_at).getTime();
    return nowMs >= ko - 48 * 60 * 60 * 1000 && nowMs <= ko + 48 * 60 * 60 * 1000;
  });

  // Refetch fixtures + leaderboards.
  const refresh = async () => {
    if (!comp || !activeGw) return;
    try {
      const [fx, lbW, lbM, lbS] = await Promise.all([
        api.get(`/competitions/${id}/fixtures?gameweek_id=${activeGw.id}`),
        api.get(`/leaderboards/${id}/weekly?gameweek_id=${activeGw.id}`),
        api.get(`/leaderboards/${id}/monthly${selectedMonthId ? `?month_id=${selectedMonthId}` : ""}`),
        api.get(`/leaderboards/${id}/season`),
      ]);
      setFixtures(fx.data);
      setLbWeekly(lbW.data); setLbMonthly(lbM.data); setLbSeason(lbS.data);
    } catch { /* ignore */ }
  };

  // Live polling every 30s while any fixture is in the live window.
  useEffect(() => {
    if (!inLiveWindow) return;
    const iv = setInterval(refresh, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inLiveWindow, comp, activeGw, id, selectedMonthId]);

  // Refetch immediately when the tab becomes visible or window regains focus —
  // browsers throttle setInterval on backgrounded tabs, so this catches you up
  // the moment you look at the app again.
  useEffect(() => {
    if (!comp || !activeGw) return;
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comp, activeGw, id, selectedMonthId]);

  useEffect(() => {
    if (!comp) return;
    (async () => {
      const monthlyUrl = selectedMonthId
        ? `/leaderboards/${id}/monthly?month_id=${selectedMonthId}`
        : `/leaderboards/${id}/monthly`;
      const [s, lbS, lbW, lbM, st] = await Promise.all([
        api.get(`/me/stats?competition_id=${id}`),
        api.get(`/leaderboards/${id}/season`),
        api.get(`/leaderboards/${id}/weekly${activeGw ? `?gameweek_id=${activeGw.id}` : ""}`),
        api.get(monthlyUrl),
        api.get(`/competitions/${id}/stats`),
      ]);
      setLbSeason(lbS.data); setLbWeekly(lbW.data); setLbMonthly(lbM.data); setStats(st.data);
    })();
  }, [comp, activeGw, id, selectedMonthId]);

  const predByFixture = useMemo(() => Object.fromEntries(predictions.map((p) => [p.fixture_id, p])), [predictions]);

  const savePrediction = async (payload) => {
    try {
      await api.put("/predictions", payload);
      const { data } = await api.get(`/predictions/mine?competition_id=${id}&gameweek_id=${activeGw.id}`);
      setPredictions(data);
    } catch (e) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!comp) return <div className="text-[#AAB7C7]">Loading…</div>;

  const tabs = [
    { id: "fixtures", label: "Predict" },
    { id: "leaderboards", label: "Leaderboards" },
    { id: "results", label: "Results" },
    { id: "stats", label: "Statistics" },
  ];

  const monthlyOn = comp?.monthly_enabled !== false;
  const jokerOn = comp?.joker_enabled !== false;
  const completedFixtures = fixtures.filter((f) => f.status === "complete" || f.is_live);

  return (
    <div className="fade-up space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <Link to="/app/competitions" className="text-xs text-[#C7F500] hover:underline">← All competitions</Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">{comp.name}</h1>
          <p className="text-sm text-[#AAB7C7]">{comp.season} · invite code <span className="text-white font-mono">{comp.invite_code}</span></p>
        </div>
        {(comp.is_owner || user?.role === "super_admin") && (
          <Link to={`/app/admin/competitions/${comp.id}`} data-testid="manage-comp-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-sm">
            <Settings className="w-4 h-4" /> Manage
          </Link>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-[#1F3A5C]">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setParams({ tab: t.id })} data-testid={`tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-[#C7F500] text-white" : "border-transparent text-[#AAB7C7] hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Gameweek selector */}
      {(tab === "fixtures" || tab === "results" || tab === "leaderboards") && gameweeks.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[#AAB7C7] uppercase tracking-wider">Gameweek</span>
            <GameweekSelect gameweeks={gameweeks} value={activeGw} onChange={setActiveGw} />
          </div>
          {inLiveWindow && (
            <button
              onClick={refresh}
              data-testid="refresh-scores-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30"
              title="Force refresh live scores"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          )}
        </div>
      )}

      {tab === "fixtures" && (
        <div className="space-y-3">
          <DeadlineBanner competitionId={id} />
          {(() => {
            // Joker reminder: any open fixture in this gameweek + no joker picked yet.
            const nowDate = new Date();
            const hasOpenFixture = fixtures.some((f) => {
              if (f.status === "complete") return false;
              const dl = f.deadline ? new Date(f.deadline) : (f.kickoff_at ? new Date(f.kickoff_at) : null);
              return !dl || dl > nowDate;
            });
            const jokerPicked = predictions.some((p) => p.is_joker);
            if (!jokerOn || !fixtures.length || !hasOpenFixture || jokerPicked) return null;
            return (
              <div
                data-testid="joker-reminder"
                className="flex items-start gap-3 p-3 rounded-xl bg-[#F5B942]/10 border border-[#F5B942]/40 text-[#F5B942]"
              >
                <Star className="w-4 h-4 mt-0.5 flex-shrink-0 fill-[#F5B942] joker-pulse" />
                <div className="text-xs sm:text-sm">
                  <div className="font-semibold">You haven&apos;t picked your Joker yet.</div>
                  <div className="text-[#F5B942]/80">Tap the Joker star on one fixture below to double its points for this gameweek.</div>
                </div>
              </div>
            );
          })()}
          {fixtures.length === 0 ? (
            <p className="text-[#AAB7C7] text-sm">No fixtures for this gameweek yet.</p>
          ) : (
            fixtures.map((f) => {
              const nowDate = new Date();
              const kickoff = f.kickoff_at ? new Date(f.kickoff_at) : null;
              const deadlineDate = f.deadline ? new Date(f.deadline) : kickoff;
              const locked =
                (deadlineDate && deadlineDate < nowDate) ||
                f.status === "complete";
              return <FixtureCard key={f.id} fixture={f} prediction={predByFixture[f.id]} locked={locked} onSave={savePrediction} jokerOn={jokerOn} />;
            })
          )}
        </div>
      )}

      {tab === "leaderboards" && (
        <>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            {monthlyOn ? (
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#AAB7C7] uppercase tracking-wider">Month</label>
              <select
                value={selectedMonthId}
                onChange={(e) => setSelectedMonthId(e.target.value)}
                data-testid="monthly-selector"
                className="bg-[#0D2038] border border-[#1F3A5C] rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="">Current calendar month</option>
                {months.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            ) : <div />}
            <button
              onClick={() => {
                const gw = activeGw ? `gameweek_id=${activeGw.id}` : "";
                const mo = selectedMonthId ? `&month_id=${selectedMonthId}` : "";
                window.open(`${API}/exports/${id}/leaderboards.pdf?${gw}${mo}&t=${Date.now()}`, "_blank", "noreferrer");
              }}
              data-testid="export-leaderboards-pdf"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-sm"
            >
              <FileText className="w-4 h-4" /> Download all tables (PDF)
            </button>
          </div>

          <div className={`grid gap-5 ${monthlyOn ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
            <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold">Weekly</h3>
                <span className="text-xs text-[#AAB7C7]">GW{activeGw?.week_number ?? "—"}</span>
              </div>
              <LeaderboardTable rows={lbWeekly} currentUserId={user?.id} columns={["pos", "name", "points"]} />
            </div>
            {monthlyOn && (
            <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="monthly-table">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold">Monthly</h3>
                <span className="text-xs text-[#AAB7C7]">
                  {selectedMonthId ? months.find((m) => m.id === selectedMonthId)?.name : "Calendar"}
                </span>
              </div>
              <LeaderboardTable rows={lbMonthly} currentUserId={user?.id} columns={["pos", "name", "points"]} />
            </div>
            )}
            <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold">Season</h3>
                <a href={`${API}/exports/${id}/leaderboard.csv`} target="_blank" rel="noreferrer" data-testid="export-season-csv"
                  className="text-xs text-[#C7F500] hover:underline inline-flex items-center gap-1"><Download className="w-3 h-3" /> CSV</a>
              </div>
              <LeaderboardTable rows={lbSeason} currentUserId={user?.id} columns={jokerOn ? undefined : ["pos", "name", "points", "exact"]} />
            </div>
          </div>
        </>
      )}

      {tab === "results" && (
        <div className="space-y-3">
          {activeGw && (comp.is_owner || user?.role === "super_admin") && (
            <div className="flex justify-end">
              <button
                onClick={() => window.open(`${API}/exports/${id}/predictions.pdf?gameweek_id=${activeGw.id}&t=${Date.now()}`, "_blank", "noreferrer")}
                data-testid="export-predictions-pdf"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-sm"
              >
                <FileText className="w-4 h-4" /> Download GW{activeGw.week_number} predictions (admin PDF)
              </button>
            </div>
          )}
          {completedFixtures.length === 0 ? (
            <p className="text-[#AAB7C7] text-sm">No completed results yet.</p>
          ) : (
            completedFixtures.map((f) => {
              const p = predByFixture[f.id];
              return (
                <div key={f.id} className="p-4 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid={`result-${f.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 text-right pr-3 font-medium">{f.home_team}</div>
                    <div className="px-3 font-heading text-xl font-bold text-[#C7F500]">{f.home_score} - {f.away_score}</div>
                    <div className="flex-1 pl-3 font-medium">{f.away_team}</div>
                  </div>
                  {p && (
                    <div className="mt-2 flex items-center justify-center gap-3 text-xs text-[#AAB7C7]">
                      <span>Your prediction: {p.home_goals}-{p.away_goals}</span>
                      {p.is_joker && <span className="text-[#F5B942] inline-flex items-center gap-1"><Star className="w-3 h-3" /> Joker x2</span>}
                      <span className={`font-bold ${p.points_awarded > 0 ? "text-[#C7F500]" : "text-red-400"}`}>+{p.points_awarded} pts</span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === "stats" && stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
            <div className="flex items-center gap-2 mb-1"><Trophy className="w-4 h-4 text-[#C7F500]" /><h3 className="font-heading font-semibold">Top scorers</h3></div>
            <ol className="text-sm mt-2 space-y-1">
              {stats.top_scorers.map((s, i) => (
                <li key={s.user_id} className="flex justify-between"><span><span className="text-[#AAB7C7] mr-2">{i + 1}.</span>{s.name}</span><span className="font-bold">{s.total_points}</span></li>
              ))}
            </ol>
          </div>
          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
            <h3 className="font-heading font-semibold mb-2">Most exact scores</h3>
            <ol className="text-sm space-y-1">
              {stats.most_exact.map((s, i) => (
                <li key={s.user_id} className="flex justify-between"><span><span className="text-[#AAB7C7] mr-2">{i + 1}.</span>{s.name}</span><span className="font-bold text-[#C7F500]">{s.exact_scores}</span></li>
              ))}
            </ol>
          </div>
          {jokerOn && (
          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="most-joker-wins">
            <h3 className="font-heading font-semibold mb-2">Most Joker wins</h3>
            <ol className="text-sm space-y-1">
              {stats.most_jokers.map((s, i) => (
                <li key={s.user_id} className="flex justify-between"><span><span className="text-[#AAB7C7] mr-2">{i + 1}.</span>{s.name}</span><span className="font-bold text-[#F5B942]">{s.joker_wins}</span></li>
              ))}
            </ol>
          </div>
          )}
          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
            <div className="text-xs uppercase tracking-wider text-[#AAB7C7]">Total predictions</div>
            <div className="font-heading text-3xl font-bold mt-1">{stats.total_predictions}</div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
            <div className="text-xs uppercase tracking-wider text-[#AAB7C7]">Total exact scores</div>
            <div className="font-heading text-3xl font-bold mt-1 text-[#C7F500]">{stats.total_exact}</div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
            <div className="text-xs uppercase tracking-wider text-[#AAB7C7]">Fixtures complete</div>
            <div className="font-heading text-3xl font-bold mt-1">{stats.fixtures_complete} / {stats.fixtures_total}</div>
          </div>
        </div>
      )}
    </div>
  );
}
